import { ScrollReveal } from "./ScrollReveal";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useCallback } from "react";

const testimonials = [
  {
    name: "Mitesh Sheth",
    quote:
      "My employee closed 3 deals in the very first month of purchasing PRObroker software. The RR flow is excellent.",
    rating: 5,
    initials: "MS",
    color: "from-violet-500 to-purple-600",
  },
  {
    name: "Hemant Panchal",
    quote:
      "We were very frustrated with our old software as we were not getting proper responses and properties were repetitive. But with PRObroker, especially its ease of use and clear UI, I’m very happy.",
    rating: 5,
    initials: "HP",
    color: "from-blue-500 to-cyan-600",
  },
  {
    name: "Heli Patel",
    quote:
      "Earlier, it was difficult to manage and track daily work. Now, my employees’ time is saved and overall productivity has improved.",
    rating: 5,
    initials: "HP",
    color: "from-amber-500 to-orange-600",
  },
  {
    name: "Hit Desai",
    quote:
      "હું છેલ્લા 6 મહિનાથી PRObroker ઉપયોગ કરું છું. PRObroker ખૂબ સારું છે અને યુનિક પ્રોપર્ટી આપે છે. મારા સ્ટાફને પ્રોપર્ટી શોધવામાં બહુ સરળતા થઈ ગઈ છે.",
    rating: 5,
    initials: "HD",
    color: "from-emerald-500 to-teal-600",
  },
  {
    name: "Nil Patel",
    quote:
      "PRObroker સોફ્ટવેર વાપરવું બહુ સરળ છે અને પ્રોપર્ટી પણ ઝડપી મળે છે. તેમજ ડીલ કરવી પણ સરળ બની જાય છે.",
    rating: 5,
    initials: "NP",
    color: "from-pink-500 to-rose-600",
  },
  {
    name: "Dhruv Joshi",
    quote:
      "અમદાવાદના રિયલ એસ્ટેટ બ્રોકર્સ માટે PRObroker સૌથી સારું સોફ્ટવેર છે. Highly recommended.",
    rating: 5,
    initials: "DJ",
    color: "from-indigo-500 to-blue-600",
  },
  {
    name: "Rakesh Parmar",
    quote:
      "Genuine અને verified properties મળે છે અને દરરોજ નવી અપડેટ્સ પણ મળે છે.",
    rating: 5,
    initials: "RP",
    color: "from-yellow-500 to-orange-500",
  },
  {
    name: "Ravi Maheshwari",
    quote:
      "Properties manage કરવું ખૂબ સરળ બની જાય છે અને features પણ ખૂબ સારા છે, જેના કારણે કામ વધુ સરળ બને છે.",
    rating: 5,
    initials: "RM",
    color: "from-green-500 to-emerald-600",
  },
  {
    name: "Jayraj Patel",
    quote:
      "मेरा काम रेसिडेंशियल रेंट में है — गोता, जगतपुर और वैष्णो देवी साइड में। मुझे PRObroker से काफी मदद मिलती है और अच्छी प्रॉपर्टीज़ की अपडेट मिलती रहती है।",
    rating: 5,
    initials: "JP",
    color: "from-red-500 to-pink-600",
  },

];

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
    scale: 0.96,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
    scale: 0.96,
  }),
};

const TestimonialsSection = () => {
  const [[activeIndex, direction], setPage] = useState([0, 0]);
  const [isPaused, setIsPaused] = useState(false);

  // Two cards visible at a time on larger screens
  const visibleCount = 2;
  const totalSlides = testimonials.length;

  const paginate = useCallback(
    (dir) => {
      setPage(([prev]) => {
        const next = (prev + dir + totalSlides) % totalSlides;
        return [next, dir];
      });
    },
    [totalSlides]
  );

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => paginate(1), 3500);
    return () => clearInterval(timer);
  }, [isPaused, paginate]);

  // Get two consecutive testimonials for the current slide
  const getVisible = () => {
    return [
      testimonials[activeIndex % totalSlides],
      testimonials[(activeIndex + 1) % totalSlides],
    ];
  };

  const visible = getVisible();

  return (
    <section className="section-light py-20 sm:py-28">
      <div className="container mx-auto px-4 sm:px-6">
        <ScrollReveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground text-center mb-3">
            Testimonials
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-center mb-5 text-foreground">
            What Brokers Say About{" "}
            <span className="gradient-text font-extrabold">PRObroker</span>
          </h2>
          <p className="text-center text-muted-foreground mb-14 max-w-md mx-auto text-sm leading-relaxed">
            Hear from brokers who are growing their business every day.
          </p>
        </ScrollReveal>

        <div
          className="relative max-w-3xl mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Carousel track */}
          <div className="overflow-hidden rounded-2xl">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={activeIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.42, ease: [0.4, 0, 0.2, 1] }}
                className="grid sm:grid-cols-2 gap-5"
              >
                {visible.map((t, i) => (
                  <motion.div
                    key={t.name}
                    whileHover={{ y: -3 }}
                    className="relative p-6 rounded-2xl bg-background border border-border hover:border-primary/15 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 group"
                  >
                    <Quote className="absolute top-5 right-5 w-8 h-8 text-primary/[0.06] group-hover:text-primary/10 transition-colors" />

                    <div className="flex gap-0.5 mb-4">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <Star
                          key={j}
                          className="w-3.5 h-3.5 fill-amber-400 text-amber-400"
                        />
                      ))}
                    </div>

                    <p className="text-sm text-foreground/75 leading-relaxed mb-5">
                      "{t.quote}"
                    </p>

                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center`}
                      >
                        <span className="text-xs font-bold text-primary-foreground">
                          {t.initials}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground leading-tight">
                          {t.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t.company}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Nav buttons */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={() => paginate(-1)}
              className="w-9 h-9 rounded-full border border-border bg-background hover:bg-primary/5 hover:border-primary/30 flex items-center justify-center transition-all duration-200 group"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>

            {/* Dot indicators */}
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() =>
                    setPage(([prev]) => [i, i > prev ? 1 : -1])
                  }
                  aria-label={`Go to slide ${i + 1}`}
                  className="transition-all duration-300 rounded-full"
                  style={{
                    width: i === activeIndex ? "20px" : "8px",
                    height: "8px",
                    background:
                      i === activeIndex
                        ? "var(--color-primary, #7c3aed)"
                        : "var(--color-border, #e5e7eb)",
                  }}
                />
              ))}
            </div>

            <button
              onClick={() => paginate(1)}
              className="w-9 h-9 rounded-full border border-border bg-background hover:bg-primary/5 hover:border-primary/30 flex items-center justify-center transition-all duration-200 group"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-5 max-w-xs mx-auto h-0.5 bg-border rounded-full overflow-hidden">
            {!isPaused && (
              <motion.div
                key={activeIndex}
                className="h-full bg-primary/40 rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 3.5, ease: "linear" }}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;